import React from 'react';
import { Outlet, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Role } from '../../../shared/types/user.types';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';
import { Car, LayoutDashboard, Wrench, Users, Settings, FileText, History, Bell, LogOut, Shield, CircleCheckBig, Menu as MenuIcon, ChevronDown, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import useNotification from '../wrappers/use-notification';
import NotificationList from '../components/NotificationList';
import { NotificationFilters } from '../../../shared/types/notification.types';
import {
    appLayoutStyles,
    appLayoutMenuIconStyles,
    getAppLayoutDesktopTabStyles,
    getAppTabIndicatorStyle,
} from './AppLayout.styles';

interface NavItem {
    name: string;
    href: string;
    icon: React.ElementType;
}

const AppLayout: React.FC = () => {
    const { user, isAuthenticated, emailVerified, logout } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [bellAnchorEl, setBellAnchorEl] = useState<null | HTMLElement>(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const { list } = useNotification();
    const theme = useTheme();

    const notificationFilters: NotificationFilters = {
        userUid: user?.uid,
        garageUid: user?.garageUid,
        dealerId: user?.dealerId,
        role: user?.role,
        read: false,
    };

    useEffect(() => {
        if (!user) return;
        list(notificationFilters).then(result => setUnreadCount(result.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid, bellAnchorEl]);

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!emailVerified) {
        return <Navigate to="/email-verification" replace />;
    }

    if (user?.status) {
        return <Navigate to="/account-status" replace />;
    }

    const getNavItems = (): NavItem[] => {
        switch (user?.role as Role) {
            case 'customer':
                return [
                    { name: t('nav.myGarage'), href: '/app/garage', icon: Car },
                ];
            case 'dealer':
                return [
                    { name: t('nav.serviceDesk'), href: '/app/service-desk', icon: Wrench },
                    { name: t('nav.currentServices'), href: '/app/current-services', icon: CircleCheckBig },
                    { name: t('nav.notifications'), href: '/app/notifications', icon: Bell },
                ];
            case 'admin':
                return [
                    { name: t('nav.dashboard'), href: '/app/admin/dashboard', icon: LayoutDashboard },
                    { name: t('nav.vehicleRegistrations'), href: '/app/admin/vehicle-registrations', icon: FileText },
                    { name: t('nav.transfers'), href: '/app/admin/transfers', icon: History },
                    { name: t('nav.accounts'), href: '/app/admin/users', icon: Users },
                    { name: t('nav.settingsLists'), href: '/app/admin/settings', icon: Settings },
                    { name: t('nav.exports'), href: '/app/admin/exports', icon: FileText },
                    { name: t('nav.auditLog'), href: '/app/admin/audit-log', icon: Shield },
                ];
            default:
                return [];
        }
    };

    const navItems = getNavItems();

    const activeTabIndex = navItems.findIndex(item => location.pathname.startsWith(item.href));

    return (
        <Box sx={appLayoutStyles.root}>
            {/* Top AppBar */}
            <AppBar position="sticky" sx={appLayoutStyles.appBar} elevation={1}>
                <Container maxWidth="xl" disableGutters>
                    <Toolbar sx={appLayoutStyles.toolbar}>
                        {/* Logo */}
                        <Box component={Link} to="/app" sx={appLayoutStyles.logoLink}>
                            <Box sx={appLayoutStyles.logoIcon}>
                                <Car size={20} color="white" />
                            </Box>
                            <Typography variant="h6" fontWeight={700} color="primary.main">{t('common.appName')}</Typography>
                        </Box>

                        {/* Desktop right side */}
                        <Box sx={appLayoutStyles.desktopRightSection}>
                            <IconButton size="small" onClick={(e) => setBellAnchorEl(e.currentTarget)}>
                                <Badge badgeContent={unreadCount} color="error" max={99}>
                                    <Bell size={24} />
                                </Badge>
                            </IconButton>
                            <Popover
                                open={Boolean(bellAnchorEl)}
                                anchorEl={bellAnchorEl}
                                onClose={() => setBellAnchorEl(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                slotProps={{ paper: { sx: appLayoutStyles.notificationsPopoverPaper } }}
                            >
                                <Box sx={appLayoutStyles.notificationsHeader}>
                                    <Typography variant="subtitle2" fontWeight={600}>{t('notifications.title')}</Typography>
                                </Box>
                                <Box sx={appLayoutStyles.notificationsContent}>
                                    <NotificationList
                                        filters={notificationFilters}
                                        onRead={() => setUnreadCount(prev => Math.max(0, prev - 1))}
                                    />
                                </Box>
                                <Divider />
                                <Box sx={appLayoutStyles.notificationsFooter}>
                                    <Button fullWidth size="small" onClick={() => { setBellAnchorEl(null); navigate('/app/notifications'); }}>
                                        {t('notificationList.showAll')}
                                    </Button>
                                </Box>
                            </Popover>

                            <Box sx={appLayoutStyles.profileTrigger} onClick={(e) => setAnchorEl(e.currentTarget)}>
                                <Box sx={appLayoutStyles.profileTextWrapper}>
                                    <Typography variant="body2" fontWeight={600}>{user?.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{user?.role ? t(`role.${user.role}`) : ''}</Typography>
                                </Box>
                                <Avatar sx={appLayoutStyles.profileAvatar}>
                                    {user?.name?.charAt(0)?.toUpperCase()}
                                </Avatar>
                                <ChevronDown size={20} color="#757575" />
                            </Box>

                            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
                                <MenuItem component={Link} to="/app/profile" onClick={() => setAnchorEl(null)}>
                                    <User size={16} style={appLayoutMenuIconStyles} /> {t('user.myProfile')}
                                </MenuItem>
                                <MenuItem component={Link} to="/app/notifications" onClick={() => setAnchorEl(null)}>
                                    <Bell size={16} style={appLayoutMenuIconStyles} /> {t('notifications.title')}
                                </MenuItem>
                                <Divider />
                                <MenuItem onClick={() => { logout(); setAnchorEl(null); }} sx={appLayoutStyles.logoutMenuItem}>
                                    <LogOut size={16} style={appLayoutMenuIconStyles} /> {t('auth.signOut')}
                                </MenuItem>
                            </Menu>
                        </Box>

                        {/* Mobile hamburger */}
                        <IconButton sx={appLayoutStyles.mobileMenuButton} onClick={() => setMobileOpen(true)}>
                            <MenuIcon />
                        </IconButton>
                    </Toolbar>
                </Container>

                {/* Secondary nav tabs - desktop */}
                <Box sx={appLayoutStyles.desktopTabsWrapper}>
                    <Container maxWidth="xl" disableGutters>
                        <Tabs
                            value={activeTabIndex >= 0 ? activeTabIndex : false}
                            sx={appLayoutStyles.desktopTabs}
                            TabIndicatorProps={{ style: getAppTabIndicatorStyle(theme) }}
                        >
                            {navItems.map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <Tab
                                        key={item.name}
                                        component={Link}
                                        to={item.href}
                                        label={
                                            <Box sx={appLayoutStyles.desktopTabLabel}>
                                                <Icon size={16} />
                                                {item.name}
                                            </Box>
                                        }
                                        sx={getAppLayoutDesktopTabStyles(activeTabIndex === index)}
                                    />
                                );
                            })}
                        </Tabs>
                    </Container>
                </Box>
            </AppBar>

            {/* Mobile drawer */}
            <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)}>
                <Box sx={appLayoutStyles.mobileDrawer}>
                    <Box sx={appLayoutStyles.mobileDrawerUserRow}>
                        <Avatar sx={appLayoutStyles.mobileDrawerAvatar}>{user?.name?.charAt(0)}</Avatar>
                        <Box>
                            <Typography variant="body2" fontWeight={600}>{user?.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                        </Box>
                    </Box>
                    <Divider />
                    <List>
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            const active = location.pathname.startsWith(item.href);
                            return (
                                <ListItem key={item.name} disablePadding>
                                    <ListItemButton
                                        component={Link}
                                        to={item.href}
                                        onClick={() => setMobileOpen(false)}
                                        selected={active}
                                    >
                                        <ListItemIcon><Icon size={20} /></ListItemIcon>
                                        <ListItemText primary={item.name} />
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                    <Divider />
                    <List>
                        <ListItem disablePadding>
                            <ListItemButton onClick={() => { logout(); setMobileOpen(false); }}>
                                <ListItemIcon><LogOut size={20} color="#d32f2f" /></ListItemIcon>
                                <ListItemText primary={t('auth.signOut')} primaryTypographyProps={{ color: 'error.main' }} />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Box>
            </Drawer>

            {/* Main content */}
            <Container maxWidth="xl" sx={appLayoutStyles.mainContent}>
                <Outlet />
            </Container>

            {/* Footer */}
            <Box component="footer" sx={appLayoutStyles.footer}>
                <Container maxWidth="xl" sx={appLayoutStyles.footerContainer}>
                    <Typography variant="caption" color="text.secondary">{t('mainLayout.footerCopyright', { year: new Date().getFullYear() })}</Typography>
                </Container>
            </Box>
        </Box>
    );
};

export default AppLayout;
